// src/components/Control/Operator/Header.tsx
// Шапка: робот, связь, Zenoh, батарея, режим, голос, настройки и STOP.
// STOP виден всегда и работает без подтверждения. У Pi без шасси нет ни STOP,
// ни батареи, ни автономии: ехать нечему.
import React from 'react';
import { useHasChassis, useRobot, useUi, useUiActions, useVoiceState } from '../robot/RobotContext';
import { MODE_NAMES } from '../robot/telemetry';
import { Dot, Icon, usePopover } from './ui';
import { useRobotStatus, RobotStatus } from './status';

export const StopButton: React.FC<{ compact?: boolean; className?: string }> = ({ compact, className }) => {
  const { stop } = useRobot();
  return (
    <button type="button" className={`op-stop ${compact ? 'compact' : ''} ${className ?? ''}`} onClick={stop}
            aria-label="Аварийная остановка робота (STOP)" title="Аварийная остановка: движение, автономия и поездка к точке (Пробел / Esc)">
      <span className="op-stop-icon" aria-hidden="true">■</span>
      <span>STOP</span>
    </button>
  );
};

export const MicButton: React.FC = () => {
  const { voice } = useRobot();
  const { toggleVoice } = useUiActions();
  const open = useUi(u => u.voiceOpen);
  const phase = useVoiceState(v => v.phase);
  const active = open && phase === 'listening';
  return (
    <button type="button" className={`op-icon-btn ${active ? 'live' : ''}`}
            aria-label={active ? 'Отменить голосовую команду' : 'Голосовая команда'} aria-pressed={active}
            title="Голосовая команда"
            onClick={() => {
              if (active) {
                voice.cancel();
              } else {
                toggleVoice(true);
                voice.startCommand();
              }
            }}>
      <Icon name="mic" />
    </button>
  );
};

const MODES: [string, string][] = [
  ['explore', 'Объезд комнаты'],
  ['approach', 'Подъехать к человеку'],
  ['follow', 'Следовать за человеком'],
];

// Режим автономии: текущий и запуск
export const ModeMenu: React.FC<{ s: RobotStatus; compact?: boolean }> = ({ s, compact }) => {
  const { session, robotId } = useRobot();
  const pop = usePopover();
  const setMode = (mode: string) => {
    session?.put(`robot/${robotId}/mode`, mode).catch(err => console.warn('mode put failed', err));
    pop.setOpen(false);
  };
  const m = s.mission;
  return (
    <div className="op-pop-wrap" ref={pop.ref}>
      <button type="button" className={`op-chip mode ${s.modeLevel}`} aria-haspopup="menu" aria-expanded={pop.open}
              onClick={() => pop.setOpen(!pop.open)} title="Режим робота">
        <Dot level={s.modeLevel} />
        <span className="op-chip-text">{compact ? (s.autonomous ? MODE_NAMES[s.mode!] : s.modeText) : s.modeText}</span>
        <Icon name="chevronDown" size={14} />
      </button>
      {pop.open && (
        <div className="op-popover" role="menu">
          <div className="op-pop-title">Автономия</div>
          {!m ? (
            <p className="op-muted">Автономия не на связи (mission_node на Pi не запущен?)</p>
          ) : (
            <p className="op-muted">
              Режим: <b>{MODE_NAMES[m.mode] ?? m.mode}</b>
              {m.detail ? ` · ${m.detail}` : ''}
              {m.front != null ? ` · впереди свободно ${Math.round(m.front * 100)} см` : ''}
              {m.person ? ` · ${m.person.name ?? 'человек'} ${Math.round(m.person.fill * 100)}% кадра${m.person.hands_up ? ', руки вверх' : ''}` : ''}
            </p>
          )}
          {MODES.map(([mode, name]) => (
            <button key={mode} type="button" role="menuitemradio" aria-checked={s.mode === mode}
                    className={`op-menu-item ${s.mode === mode ? 'on' : ''}`} disabled={!m} onClick={() => setMode(mode)}>
              {name}
            </button>
          ))}
          <button type="button" role="menuitem" className="op-menu-item danger" onClick={() => setMode('idle')}>
            Ожидание — остановить автономию
          </button>
        </div>
      )}
    </div>
  );
};

export const ConnChip: React.FC<{ s: RobotStatus; compact?: boolean }> = ({ s, compact }) => {
  const { openSettings } = useUiActions();
  return (
    <button type="button" className={`op-chip ${s.conn.level}`} title={s.conn.detail}
            onClick={() => openSettings('diagnostics')} aria-label={`Связь с роботом: ${s.conn.text}. ${s.conn.detail}`}>
      <Dot level={s.conn.level} />
      {!compact && <span className="op-chip-text">{s.conn.text}</span>}
    </button>
  );
};

export const BatteryChip: React.FC<{ s: RobotStatus }> = ({ s }) => {
  const b = s.battery;
  const level = b == null ? 'off' : b < 15 ? 'error' : b < 30 ? 'warn' : 'ok';
  return (
    <span className={`op-chip ${level}`} title={s.voltage != null ? `${s.voltage} В` : 'Батарея'}
          aria-label={`Батарея: ${b == null ? 'нет данных' : `${b}%`}${s.charging ? ', заряжается' : ''}`}>
      <span className="op-batt" aria-hidden="true"><i style={{ width: `${Math.max(0, Math.min(100, b ?? 0))}%` }} /></span>
      <span className="op-chip-text">{b == null ? '—' : `${b}%`}{s.charging ? ' ⚡' : ''}</span>
    </span>
  );
};

const Header: React.FC<{ variant: 'desktop' | 'mobile' }> = ({ variant }) => {
  const { robotName, goBack } = useRobot();
  const s = useRobotStatus();
  const chassis = useHasChassis();
  const screen = useUi(u => u.screen);
  const { go, openSettings, closeSettings } = useUiActions();

  if (variant === 'mobile') {
    return (
      <header className="op-header mobile">
        {screen === 'settings' ? (
          <button type="button" className="op-icon-btn" onClick={closeSettings} aria-label="Закрыть настройки"><Icon name="back" /></button>
        ) : (
          <button type="button" className="op-icon-btn" onClick={goBack} aria-label="К списку роботов"><Icon name="back" /></button>
        )}
        <div className="op-robot">
          <span className="op-robot-name">{robotName}</span>
          <span className={`op-robot-state ${s.conn.level}`}><Dot level={s.conn.level} /> {s.conn.text}</span>
        </div>
        <MicButton />
        {chassis && <BatteryChip s={s} />}
        <button type="button" className={`op-icon-btn ${screen === 'settings' ? 'on' : ''}`} aria-label="Настройки"
                onClick={() => (screen === 'settings' ? closeSettings() : openSettings())}>
          <Icon name="gear" />
        </button>
        {chassis && <StopButton compact />}
      </header>
    );
  }

  return (
    <header className="op-header">
      <button type="button" className="op-icon-btn" onClick={goBack} aria-label="К списку роботов" title="К списку роботов">
        <Icon name="back" />
      </button>
      <div className="op-robot">
        <span className="op-robot-name">{robotName}</span>
      </div>
      <ConnChip s={s} />
      <span className={`op-chip ${s.zstate === 'connected' ? 'ok' : s.zstate === 'idle' ? 'off' : 'warn'}`}
            title="Связь браузера с сервером Zenoh">
        <Dot level={s.zstate === 'connected' ? 'ok' : s.zstate === 'idle' ? 'off' : 'warn'} />
        <span className="op-chip-text">Zenoh</span>
      </span>
      {chassis && <BatteryChip s={s} />}
      {chassis && <ModeMenu s={s} />}
      <nav className="op-tabs" aria-label="Экраны">
        {chassis ? (
          <button type="button" aria-label="Карта и камера" className={screen === 'map' || screen === 'camera' ? 'on' : ''} onClick={() => go('map')}>
            <Icon name="map" size={16} /> <span className="op-tab-label">Карта и камера</span>
          </button>
        ) : (
          <button type="button" aria-label="Камера" className={screen === 'map' || screen === 'camera' ? 'on' : ''} onClick={() => go('camera')}>
            <Icon name="camera" size={16} /> <span className="op-tab-label">Камера</span>
          </button>
        )}
        <button type="button" aria-label="Няня" className={screen === 'nanny' ? 'on' : ''} onClick={() => go('nanny')}>
          <Icon name="nanny" size={16} /> <span className="op-tab-label">Няня</span>{s.nanny ? ' ●' : ''}
        </button>
        <button type="button" aria-label="Настройки" className={screen === 'settings' ? 'on' : ''}
                onClick={() => (screen === 'settings' ? closeSettings() : openSettings())}>
          <Icon name="gear" size={16} /> <span className="op-tab-label">Настройки</span>
        </button>
      </nav>
      <div className="op-spacer" />
      <MicButton />
      {chassis && <StopButton />}
    </header>
  );
};

export default Header;
